/**
 * Admin + catalogue API functions (api-contract.md §6, §8).
 *
 * Typed wrappers for the admin role surface: medicines, status master, pharmacy settings,
 * doctor lifecycle, staff accounts. Mutating POSTs marked 🔑 send an Idempotency-Key.
 */

import { apiFetch, newIdempotencyKey } from "./client";

// --- Medicines (A-07) ---

export interface Medicine {
  id: string;
  name: string;
  brand: string | null;
  schedule_class: string;
  hsn_code: string | null;
  mrp: string;
  default_gst_pct: string;
  unit: string;
  unit_kind: string;
  active: boolean;
  // Acknowledgement text set only on create/update/activate responses (for a success toast).
  message?: string | null;
}

export interface MedicineInput {
  name: string;
  brand?: string | null;
  schedule_class?: string;
  hsn_code?: string | null;
  mrp?: string;
  default_gst_pct?: string;
  unit: string;
  unit_kind: string;
}

export interface PaginatedMedicines {
  data: Medicine[];
  total: number;
  page: number;
  per_page: number;
  has_more: boolean;
  sort: string;
  descending: boolean;
}

export interface MedicineQuery {
  q?: string;
  status?: "active" | "disabled" | "";
  unit_kind?: string;
  sort?: string;
  descending?: boolean;
  page?: number;
  per_page?: number;
}

export const listMedicines = (query: MedicineQuery = {}): Promise<PaginatedMedicines> => {
  const params = new URLSearchParams();
  if (query.q?.trim()) params.set("q", query.q.trim());
  if (query.status) params.set("status", query.status);
  if (query.unit_kind) params.set("unit_kind", query.unit_kind);
  if (query.sort) params.set("sort", query.sort);
  if (query.descending) params.set("descending", "true");
  params.set("page", String(query.page ?? 1));
  params.set("per_page", String(query.per_page ?? 25));
  return apiFetch(`/api/v1/admin/medicines?${params.toString()}`);
};

export const createMedicine = (body: MedicineInput, idempotencyKey?: string): Promise<Medicine> =>
  apiFetch("/api/v1/admin/medicines", {
    method: "POST",
    body,
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });

export const updateMedicine = (id: string, body: Partial<MedicineInput>): Promise<Medicine> =>
  apiFetch(`/api/v1/admin/medicines/${id}`, { method: "PATCH", body });

export const disableMedicine = (id: string): Promise<void> =>
  apiFetch(`/api/v1/admin/medicines/${id}/disable`, { method: "POST" });

export const activateMedicine = (id: string): Promise<Medicine> =>
  apiFetch(`/api/v1/admin/medicines/${id}/activate`, { method: "POST" });

// --- Status master (A-06) ---

export interface Status {
  id: string;
  code: string;
  label: string;
  description: string | null;
  transition_type: string;
  is_final: boolean;
  is_initial: boolean;
  patient_visible: boolean;
  display_order: number;
  colour_token: string | null;
  active: boolean;
}

export const listStatuses = (): Promise<Status[]> => apiFetch("/api/v1/admin/status-master");

export const createStatus = (
  body: Partial<Status> & { code: string; label: string },
  idempotencyKey?: string,
): Promise<Status> =>
  apiFetch("/api/v1/admin/status-master", {
    method: "POST",
    body,
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });

export const updateStatus = (
  id: string,
  body: { label?: string; description?: string; colour_token?: string; display_order?: number },
): Promise<Status> => apiFetch(`/api/v1/admin/status-master/${id}`, { method: "PATCH", body });

// --- Pharmacy settings (A-13) ---

export interface PharmacySettings {
  id: string;
  name: string;
  legal_name: string;
  address_line1: string;
  address_line2: string | null;
  city: string;
  state_code: string;
  pin_code: string;
  gstin: string;
  phone: string;
  email: string;
  default_delivery_gst_pct: string;
  pharmacy_license_number: string;
}

export const getPharmacySettings = (): Promise<PharmacySettings> =>
  apiFetch("/api/v1/admin/pharmacy-settings");

export const patchPharmacySettings = (
  body: Partial<PharmacySettings>,
): Promise<PharmacySettings> =>
  apiFetch("/api/v1/admin/pharmacy-settings", { method: "PATCH", body });

// --- Doctors (A-03 / A-04) ---

export interface Doctor {
  id: string;
  full_name: string;
  phone: string | null;
  email: string | null;
  status: string;
  state_medical_council: string;
  specialisation: string | null;
  verification_checklist: Record<string, boolean>;
  verified_at: string | null;
  license_number_masked: string;
  // May the doctor sign in? Off for doctors a pharmacist added, until an admin turns it on.
  login_enabled: boolean;
  added_by_role: string | null; // "admin" | "pharmacist"; null for doctors onboarded earlier
  added_by_name: string | null;
}

export interface SignupLink {
  user_id: string;
  expires_at: string;
  signup_token_dev: string | null;
}

export interface PaginatedDoctors {
  data: Doctor[];
  total: number;
  page: number;
  per_page: number;
  has_more: boolean;
  sort: string;
  descending: boolean;
}

export interface DoctorQuery {
  q?: string; // name, or 3+ digits of the phone
  status?: "" | "pending_verification" | "active" | "suspended";
  verified?: boolean | null;
  login_enabled?: boolean | null;
  added_by?: "" | "admin" | "pharmacist";
  sort?: string;
  descending?: boolean;
  page?: number;
  per_page?: number;
}

export const listDoctors = (query: DoctorQuery = {}): Promise<PaginatedDoctors> => {
  const params = new URLSearchParams();
  if (query.q?.trim()) params.set("q", query.q.trim());
  if (query.status) params.set("status", query.status);
  if (query.verified === true) params.set("verified", "true");
  if (query.verified === false) params.set("verified", "false");
  if (query.login_enabled === true) params.set("login_enabled", "true");
  if (query.login_enabled === false) params.set("login_enabled", "false");
  if (query.added_by) params.set("added_by", query.added_by);
  if (query.sort) params.set("sort", query.sort);
  if (query.descending) params.set("descending", "true");
  params.set("page", String(query.page ?? 1));
  params.set("per_page", String(query.per_page ?? 25));
  return apiFetch(`/api/v1/admin/doctors?${params.toString()}`);
};

export const createDoctor = (body: {
  full_name: string;
  phone: string;
  email?: string | null;
  state_medical_council: string;
  license_number: string;
  specialisation?: string | null;
  verification_checklist?: Record<string, boolean>;
}, idempotencyKey?: string): Promise<Doctor> =>
  apiFetch("/api/v1/admin/doctors", {
    method: "POST",
    body,
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });

export const updateDoctor = (
  id: string,
  body: {
    full_name?: string;
    email?: string | null;
    state_medical_council?: string;
    specialisation?: string | null;
    registration_date?: string | null;
    registration_expiry?: string | null;
  },
): Promise<Doctor> =>
  apiFetch(`/api/v1/admin/doctors/${id}`, { method: "PATCH", body });

export const setDoctorChecklist = (id: string, items: Record<string, boolean>): Promise<Doctor> =>
  apiFetch(`/api/v1/admin/doctors/${id}/verification-checklist`, {
    method: "POST",
    body: { items },
  });

export const activateDoctor = (id: string): Promise<SignupLink> =>
  apiFetch(`/api/v1/admin/doctors/${id}/activate`, { method: "POST" });

export const suspendDoctor = (id: string): Promise<Doctor> =>
  apiFetch(`/api/v1/admin/doctors/${id}/suspend`, { method: "POST" });

export const reactivateDoctor = (id: string): Promise<Doctor> =>
  apiFetch(`/api/v1/admin/doctors/${id}/reactivate`, { method: "POST" });

/** Admin-set password; the doctor is asked to change it after signing in, and is signed out
 *  everywhere. Also clears a lockout; a pending or suspended doctor keeps that status. */
export const resetDoctorPassword = (id: string, new_password: string): Promise<Doctor> =>
  apiFetch(`/api/v1/admin/doctors/${id}/reset-password`, { method: "POST", body: { new_password } });

/** The doctors-list filters, sent to act on every doctor they match ("select all matching"). */
export interface DoctorListFilter {
  q?: string | null;
  status?: "pending_verification" | "active" | "suspended" | null;
  verified?: boolean | null;
  login_enabled?: boolean | null;
  added_by?: "admin" | "pharmacist" | null;
}

export interface DoctorLoginAccessResult {
  login_enabled: boolean;
  matched: number; // doctors the request selected
  updated: number; // of those, the ones whose setting changed
}

/** Turn sign-in on/off for the listed doctors, or for every doctor matching `filter`. Turning it
 *  off signs them out everywhere; turning it on sends them nothing. */
export const setDoctorLoginAccess = (
  body: { login_enabled: boolean } & ({ doctor_ids: string[] } | { filter: DoctorListFilter }),
): Promise<DoctorLoginAccessResult> =>
  apiFetch("/api/v1/admin/doctors/login-access", { method: "POST", body });

export const DOCTOR_CHECKLIST_ITEMS = [
  "state_council_verified",
  "reg_number_matches",
  "photo_matches",
  "certificate_valid",
] as const;

// --- Staff (A-05) ---

export interface Staff {
  id: string;
  role: string;
  full_name: string;
  phone: string | null;
  email: string | null;
  status: string;
  must_change_password: boolean;
  created_at: string;
}

export const listStaff = (role: string): Promise<Staff[]> =>
  apiFetch(`/api/v1/admin/staff?role=${role}`);

export const createStaff = (body: {
  role: string;
  full_name: string;
  phone: string;
  /** Optional: a blank one is left out rather than sent as "" (ADM-03). */
  email?: string;
  state_pharmacy_council?: string;
  registration_number?: string;
}, idempotencyKey?: string): Promise<SignupLink> =>
  apiFetch("/api/v1/admin/staff", {
    method: "POST",
    body: { ...body, email: body.email?.trim() || undefined },
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });

export const updateStaff = (
  id: string,
  body: { full_name?: string; phone?: string; email?: string | null },
): Promise<Staff> =>
  apiFetch(`/api/v1/admin/staff/${id}`, { method: "PATCH", body });

/** Admin-set password; the user is asked to change it after signing in, and is signed out
 *  everywhere. Also clears a lockout and activates an account still waiting on its first password. */
export const resetStaffPassword = (id: string, new_password: string): Promise<Staff> =>
  apiFetch(`/api/v1/admin/staff/${id}/reset-password`, { method: "POST", body: { new_password } });

export const unlockStaff = (id: string): Promise<Staff> =>
  apiFetch(`/api/v1/admin/staff/${id}/unlock`, { method: "POST" });

export const suspendStaff = (id: string): Promise<Staff> =>
  apiFetch(`/api/v1/admin/staff/${id}/suspend`, { method: "POST" });

export const activateStaff = (id: string): Promise<Staff> =>
  apiFetch(`/api/v1/admin/staff/${id}/activate`, { method: "POST" });

export const reissueSignupLink = (id: string): Promise<SignupLink> =>
  apiFetch(`/api/v1/admin/staff/${id}/reissue-signup-link`, { method: "POST" });
